<script setup lang="ts">
import NewsSidebar from "~/components/Sidebars/NewsSidebar.vue";
import Paychecks from "~/components/Paycheck/Paychecks.vue";
import { setTokens } from "~/utils/authHelpers.js";

const defaultStore = useDefaultStore();
const loading = ref(true);
const route = useRoute();
const { $trpc } = useNuxtApp();

const user = computed(() => {
  return defaultStore.user;
});

const notificationSidebarOpen = computed(() => {
  return defaultStore.notificationSidebarOpen;
});

watch(
  () => route.fullPath,
  () => {
    if (user.value.userId) {
      defaultStore.closeNotificationSidebar();
      defaultStore.refreshNotifications();
    }
  },
);

watch(
  user,
  (newUser, oldUser) => {
    if (!oldUser?.userId && newUser?.userId) {
      initData();
    }
  },
  {
    immediate: true,
  },
);

function initData() {
  defaultStore.refreshNotifications();
}

function getAccessToken() {
  const routeAccessToken = route.query?.access_token ?? "";
  if (routeAccessToken) {
    return routeAccessToken;
  }

  if (localStorage.getItem("access_token")) {
    return localStorage.getItem("access_token");
  }
}

function getRefreshToken() {
  const refreshToken = route.query?.refresh_token ?? "";

  if (refreshToken) {
    return refreshToken;
  }
  if (localStorage.getItem("refresh_token")) {
    return localStorage.getItem("refresh_token");
  }
}

function getSessionToken() {
  const refreshToken = route.query?.session_token ?? "";

  if (refreshToken) {
    return refreshToken;
  }
  if (localStorage.getItem("session_token")) {
    return localStorage.getItem("session_token");
  }
}

async function checkIfRedirectedFromPartnerPage(query: typeof route.query) {
  const isRedirectedFromPartnerPage = query?.signin_from_partner;
  if (isRedirectedFromPartnerPage) {
    if (user.value.userId) {
      if (route.query.utm_campaign || query.invite_id) {
        $trpc.trackUserLanding.mutate({
          landingPage: String(isRedirectedFromPartnerPage),
          utm_campaign: String(query.utm_campaign),
          utm_source: String(query.utm_source),
          utm_medium: String(query.utm_medium),
          utm_content: String(query.utm_content),
          utm_term: String(query.utm_term),
          invite_id: String(query.invite_id),
        });
      }
      return { stopLoading: true };
    }

    try {
      const twitterResponse = await apiService.getTwitterLoginUrl({
        trigger: "button_signup_clicked",
        partnerName: String(query.signin_from_partner),
        domainType: "partner",
        deviceId: useAmplitudeStore().deviceId,
        utm_campaign: String(query.utm_campaign),
        utm_source: String(query.utm_source),
        utm_medium: String(query.utm_medium),
        utm_content: String(query.utm_content),
        utm_term: String(query.utm_term),
        invite_id: String(query.invite_id),
      });
      const { redirectUrl } = twitterResponse.data;
      window.location.href = redirectUrl;
      return { stopLoading: false };
    } catch (error) {
      console.error(error);
      return { stopLoading: true };
    }
  }
  return { stopLoading: true };
}

onMounted(() => {
  console.log("default layout onMounted");

  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  const sessionToken = getSessionToken();
  const query = route.query;

  // ugly hack: keep some known search params
  useRouter().replace({
    query: route.query?.modal_to_open
      ? { modal_to_open: route.query.modal_to_open }
      : route.query?.follow_redirect
        ? { follow_redirect: route.query.follow_redirect }
        : {},
  });

  if (accessToken && refreshToken) {
    setTokens(accessToken, refreshToken, sessionToken);

    defaultStore.loadUser().finally(async () => {
      checkIfRedirectedFromPartnerPage(query).finally(() => {
        console.log("loading.value = false because we have user");
        loading.value = false;
      });
    });
  } else {
    checkIfRedirectedFromPartnerPage(query).then((result) => {
      if (result && result.stopLoading) {
        loading.value = false;
      }
    });
  }
});
</script>

<template>
  <PageLoader v-if="loading" />
  <EvangelistLanding v-else-if="!user.userId" />
  <div v-else class="text-sm font-semibold">
    <div style="min-height: 100vh">
      <AppHeader />
      <div class="mt-2 lg:mt-4">
        <NuxtPage />
      </div>
      <Sidebar
        v-if="notificationSidebarOpen"
        @close="defaultStore.closeNotificationSidebar"
      >
        <NewsSidebar />
      </Sidebar>
    </div>
    <Paychecks />
    <GlobalTweetDetailSidebar />
    <FtuModal />
    <WithdrawalOtpModal />
    <VerifyEmailOtpModal />
    <VerifyTfaOtpModal />
    <NewPostModal />
    <EligibilityPassedModal />
    <GlobalCampaignDetailSidebar />
    <GlobalVerifyFollowingOfXAccount />
    <GlobalFAQ />
  </div>
  <SpoofingBanner
    v-if="user?.realUserId"
    :name="user.userName"
    :email="user.email"
    :handle="user.twitterHandle"
  />
</template>
